/**
 * ============================================================================
 * DEV-MODE FIXTURES — the seeded world behind `VITE_DEV_MODE=true`
 * ============================================================================
 * One coherent company (Northwind Studio, `ws-dev-1`) rendered across every
 * surface: saved posts, collections, the approval Feed, connected accounts,
 * case studies, the marketing calendar, the Fact Bank, generation history.
 *
 * WHY THIS EXISTS
 * `api.ts` had 125 DEV_MODE branches; nine of them were a hardcoded `return []`
 * — not "empty until you add something" but *incapable* of ever being
 * non-empty. Four real bugs shipped past a full session of "verification"
 * because the mock was emptier, faster and more successful than reality, so the
 * broken feature looked fine. A mock that cannot fail cannot catch anything.
 *
 * The rules this module holds to:
 *   - COHERENT. One workspace, one brand voice, one set of platforms, dates
 *     that make sense relative to each other. Incoherent seed data is its own
 *     kind of lie — the dashboard used to celebrate 42 posts while /posts said
 *     zero.
 *   - DERIVED, NOT FABRICATED. Dashboard stats are computed from the seeded
 *     posts (see `deriveDashboardStats`), so the numbers can never drift from
 *     the data again.
 *   - FALLIBLE. `devFault()` lets a tester force any endpoint to fail, hang or
 *     come back empty, so error and empty states are reachable in dev at all.
 *
 * DEV-ONLY. Every export is either pure data or guarded by `DEV_MODE` (a
 * build-time-constant `false` in production), so the whole module is
 * tree-shaken out of a prod build. Nothing here can change prod behaviour.
 * ============================================================================
 */
import { DEV_MODE } from '@/lib/devMode';
import type {
  AutopilotEntry,
  CampaignEntryOut,
  CaseStudyDetailResponse,
  CaseStudyListItem,
  Collection,
  ConnectedSocialAccount,
  DashboardStats,
  Fact,
  GenerationSessionDetail,
  GenerationSessionSummary,
  Platform,
  PrincipleSelectionEvent,
  SavedPost,
  WorkspaceExample,
  WorkspaceKeyword,
} from '@/types';

export const DEV_WORKSPACE_ID = 'ws-dev-1';

// ── Clock ───────────────────────────────────────────────────────────────────
// Everything is relative to load time, so the seed never rots into a fixture
// full of last-year's dates. `daysAgo(3)` reads the same in June as in January.
const DAY = 86_400_000;
const daysAgo = (n: number) => new Date(Date.now() - n * DAY).toISOString();
const dateOnly = (iso: string) => iso.slice(0, 10);
const inDays = (n: number) => dateOnly(new Date(Date.now() + n * DAY).toISOString());

// ── Storage keys ────────────────────────────────────────────────────────────
export const DEV_KEYS = {
  posts: 'dev_posts',
  collections: 'dev_collections',
  facts: `dev_ws_facts_${DEV_WORKSPACE_ID}`,
  sessions: `dev_ws_sessions_${DEV_WORKSPACE_ID}`,
  feed: `dev_ws_feed_${DEV_WORKSPACE_ID}`,
  social: 'dev_social_accounts',
  keywords: `dev_ws_keywords_${DEV_WORKSPACE_ID}`,
} as const;

/** SEO keywords the case-study engine targets. */
export function buildDevKeywords(): WorkspaceKeyword[] {
  return [
    { term: 'design system', source: 'brief', recently_used: true },
    { term: 'tokens-as-code', source: 'brief', recently_used: true },
    { term: 'design ops', source: 'brief', recently_used: false },
    { term: 'component library', source: 'custom', recently_used: false },
  ];
}

/** Read + parse a dev_* key. Returns `fallback` on absent/corrupt JSON. */
export function devRead<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

/** Write a dev_* key. Silently no-ops if localStorage is unavailable. */
export function devWrite(key: string, value: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode / quota. Dev-only — degrade to in-memory-less, never throw.
  }
}

// ============================================================================
// FAULT INJECTION — `window.__zupaDev`
// ============================================================================
// The mocks always succeeded, so no error state in this app had ever been seen
// in dev. Every seeded endpoint routes through `devFault()`, which consults
// this registry, so any of them can be made to fail, hang or return empty
// without touching code.

type FaultMode = 'error' | 'empty' | 'slow';

const faults = new Map<string, FaultMode>();
/** 'real' = honest latency (default). 'fast' = collapse delays, for tight loops. */
let speedMode: 'real' | 'fast' = 'real';

export class DevFaultError extends Error {
  constructor(endpoint: string) {
    super(`[dev fault] ${endpoint} was forced to fail`);
    this.name = 'DevFaultError';
  }
}

/**
 * Gate every seeded DEV_MODE branch. Throws when the endpoint is faulted to
 * 'error'; stalls for 30s on 'slow' (long enough to expose any missing loading
 * or timeout state). Returns true when the caller should return its EMPTY
 * value — that is the one outcome the caller has to shape itself, since "empty"
 * is `[]` for a list but `{ posts: [], total: 0, ... }` for a page.
 */
export async function devFault(endpoint: string): Promise<boolean> {
  const mode = faults.get(endpoint);
  if (!mode) return false;
  if (mode === 'error') throw new DevFaultError(endpoint);
  if (mode === 'slow') await new Promise((r) => setTimeout(r, 30_000));
  return mode === 'empty';
}

/**
 * Dev-mode network delay. Honest by default: these are set to what the real
 * calls actually take, because a 2.5s mock for a 10-40s Gemini run is why the
 * onboarding dead-wait shipped unnoticed. `__zupaDev.speed('fast')` collapses
 * them when you are iterating and don't need the slow path.
 */
export function devDelay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, speedMode === 'fast' ? Math.min(ms, 50) : ms));
}

interface DevHarness {
  fail: (endpoint: string, mode?: FaultMode) => string;
  clear: (endpoint?: string) => string;
  list: () => Record<string, FaultMode>;
  speed: (mode?: 'real' | 'fast') => string;
  reseed: () => string;
  endpoints: () => string[];
}

/** Every endpoint name accepted by `__zupaDev.fail()`. */
export const FAULTABLE = [
  'getPosts', 'getFavorites', 'getPost', 'createPost', 'deletePost',
  'getCollections', 'getDashboardStats', 'getSocialAccounts', 'getFeed',
  'listCaseStudies', 'getCaseStudy', 'getCampaignCalendarEntries', 'getFacts',
  'listWorkspaceExamples', 'getPendingAutopilot', 'getPendingDisruptions',
  'checkDisruptionsNow', 'getGenerationSessions', 'getGenerationSession',
  'generatePosts', 'generateAIImage', 'generatePersonas', 'researchCompany',
  'getContentIdeas',
] as const;

/** Attach `window.__zupaDev`. No-op in production. */
export function installDevHarness(reseed: () => void): void {
  if (!DEV_MODE || typeof window === 'undefined') return;
  const harness: DevHarness = {
    fail: (endpoint, mode = 'error') => {
      faults.set(endpoint, mode);
      return `${endpoint} → ${mode}. Reload or re-trigger the call to see it.`;
    },
    clear: (endpoint) => {
      if (endpoint) {
        faults.delete(endpoint);
        return `${endpoint} restored.`;
      }
      faults.clear();
      return 'All faults cleared.';
    },
    list: () => Object.fromEntries(faults),
    speed: (mode) => {
      if (mode) speedMode = mode;
      return `speed: ${speedMode}${mode ? '' : ' (pass "fast" or "real" to change)'}`;
    },
    reseed: () => {
      reseed();
      return 'Re-seeded. Reload to pick it up.';
    },
    endpoints: () => [...FAULTABLE],
  };
  (window as unknown as { __zupaDev: DevHarness }).__zupaDev = harness;
}

// ============================================================================
// IMAGES — generated at seed time, not inlined
// ============================================================================

/**
 * A brand-coloured gradient PNG, drawn on a canvas and returned as raw base64
 * (no data: prefix — that is the shape `SavedPost.image_base64` carries).
 * Generated rather than inlined so the fixtures stay readable and the images
 * stay crisp at any size. Returns null without a DOM, in which case the post
 * simply has no image — which is honest, not a crash.
 */
function brandImage(from: string, to: string): string | null {
  if (typeof document === 'undefined') return null;
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 512;
    canvas.height = 512;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    const grad = ctx.createLinearGradient(0, 0, 512, 512);
    grad.addColorStop(0, from);
    grad.addColorStop(1, to);
    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, 512, 512);
    return canvas.toDataURL('image/png').split(',')[1] ?? null;
  } catch {
    return null;
  }
}

// ============================================================================
// THE POSTS — the spine of the seeded world
// ============================================================================
// Everything else agrees with these: collections count them, the dashboard
// derives every number from them, and the tour's output pages have something
// in them because these exist.

const COLLECTION_LAUNCH = 'col-dev-1';
const COLLECTION_EVERGREEN = 'col-dev-2';

interface PostSeed {
  id: string;
  platform: Platform;
  content: string;
  original_text: string;
  principles_used: string[];
  explanation: string;
  hashtags: string[];
  tone_used: string;
  is_favorited: boolean;
  collection_id: string | null;
  daysAgo: number;
  image?: [string, string];
}

const POST_SEEDS: PostSeed[] = [
  {
    id: 'post-dev-1',
    platform: 'linkedin',
    content:
      "Most design systems die in a Figma file nobody opens.\n\nWe moved ours to tokens-as-code this quarter — defined once, consumed everywhere. Designers and engineers finally read from the same source.\n\nWeek one: 14 components migrated, 3 things broke, all 3 fixed the same day. That last part is the point. When the definitions live in one place, a break is a one-line fix instead of an archaeology dig.\n\nThe whole pitch is: stop hand-syncing.",
    original_text: 'post about why we moved to tokens-as-code and what happened in week one',
    principles_used: ['Social Proof', 'Authority'],
    explanation:
      'Opens with a contrarian observation (pattern interrupt), then earns the claim with a concrete first-week result — including what broke. Admitting the breakage is what makes the authority read as credible rather than salesy.',
    hashtags: ['designsystems', 'devx', 'designops'],
    tone_used: 'professional',
    is_favorited: true,
    collection_id: COLLECTION_LAUNCH,
    daysAgo: 2,
    image: ['#12263A', '#0EA5E9'],
  },
  {
    id: 'post-dev-2',
    platform: 'instagram',
    content:
      'Stop hand-syncing your colours.\n\nDefine them once. Consume them everywhere.\n\nThat is the whole pitch.',
    original_text: 'short punchy post about defining brand colours once',
    principles_used: ['Framing'],
    explanation:
      'Three lines, one idea. Instagram rewards a single claim delivered with total confidence — any qualifier here would dilute it.',
    hashtags: ['designsystems', 'branding'],
    tone_used: 'bold',
    is_favorited: true,
    collection_id: COLLECTION_LAUNCH,
    daysAgo: 3,
    image: ['#0EA5E9', '#111827'],
  },
  {
    id: 'post-dev-3',
    platform: 'linkedin',
    content:
      'A question for design leads:\n\nhow many times has your team rebuilt the same button this quarter?\n\nWe got tired of counting. Three teams, four rebuilds, one button — that was the week we started Northwind.\n\nIf your answer is "more than once", the problem is not your designers. It is that nothing in your stack makes the right thing the easy thing.',
    original_text: 'post about the founding story — three teams rebuilding the same button',
    principles_used: ['Curiosity Gap', 'Social Proof'],
    explanation:
      'Leads with a question the reader cannot answer comfortably, which is what earns the scroll-stop. The founding story then arrives as evidence rather than as a pitch.',
    hashtags: ['designops', 'productdesign'],
    tone_used: 'conversational',
    is_favorited: false,
    collection_id: COLLECTION_EVERGREEN,
    daysAgo: 6,
  },
  {
    id: 'post-dev-4',
    platform: 'linkedin',
    content:
      'We shipped tokens-as-code and something broke in production within the hour.\n\nHere is the honest version:\n\n- A colour alias resolved to the wrong value on dark mode\n- Two teams had pinned an old version of the package\n- Nobody had documented the migration order\n\nAll three were fixed inside a day, because the definitions live in one place now. Before, this would have been a week of grep.\n\nThe lesson is not "migrations are painless". It is that a single source of truth changes what a mistake costs you.',
    original_text: 'retro on what broke when we shipped tokens-as-code',
    principles_used: ['Authority', 'Loss Aversion'],
    explanation:
      'Volunteering the failures is the credibility move — it buys the right to make the closing claim. Loss Aversion lands in the final line, framing the benefit as the cost of a mistake rather than a feature.',
    hashtags: ['engineering', 'designsystems', 'postmortem'],
    tone_used: 'professional',
    is_favorited: false,
    collection_id: COLLECTION_EVERGREEN,
    daysAgo: 9,
    image: ['#111827', '#12263A'],
  },
  {
    id: 'post-dev-5',
    platform: 'instagram',
    content:
      'Your design system should live where engineers already work.\n\nNot in a doc nobody opens.\nNot in a Figma file with 40 unnamed layers.\n\nWhere they already are.',
    original_text: 'post about meeting engineers where they already work',
    principles_used: ['Framing'],
    explanation:
      'Repetition with a shrinking line length pulls the eye down to the payoff. The two negations do the persuading — the reader supplies the agreement themselves.',
    hashtags: ['devx', 'designsystems'],
    tone_used: 'bold',
    is_favorited: false,
    collection_id: null,
    daysAgo: 12,
  },
  {
    id: 'post-dev-6',
    platform: 'twitter',
    content:
      'design system maturity, honestly:\n\n1. a figma file\n2. a figma file + a readme\n3. a package nobody upgrades\n4. tokens in code, one source of truth\n\nmost teams think they are at 4. most are at 2.',
    original_text: 'a cheeky maturity-ladder post about design systems',
    principles_used: ['Curiosity Gap'],
    explanation:
      'The ladder invites the reader to place themselves on it, and the last line tells them they are probably wrong — which is exactly the tension that drives a reply.',
    hashtags: ['designsystems'],
    tone_used: 'witty',
    is_favorited: true,
    collection_id: null,
    daysAgo: 16,
  },
  {
    id: 'post-dev-7',
    platform: 'linkedin',
    content:
      'Onboarding a new frontend engineer used to take us two weeks before they shipped anything to the design system.\n\nIt is now three days.\n\nWe did not hire better engineers. We deleted the tribal knowledge — every token, component and rule now has exactly one definition, and it is the one the code reads.\n\nMost onboarding problems are documentation problems wearing a hat.',
    original_text: 'post about how onboarding time dropped after the migration',
    principles_used: ['Social Proof', 'Authority'],
    explanation:
      'A hard before/after number does the persuading up front, then the mechanism explains it so the number reads as earned rather than as a boast.',
    hashtags: ['engineering', 'onboarding', 'devx'],
    tone_used: 'professional',
    is_favorited: false,
    collection_id: null,
    daysAgo: 24,
  },
  {
    id: 'post-dev-8',
    platform: 'facebook',
    content:
      'We are hiring a design systems engineer.\n\nYou will own the thing that a hundred other engineers depend on every day. It is the least glamorous, highest-leverage job in the building, and we treat it that way.\n\nRemote-first. Link in the comments.',
    original_text: 'hiring post for a design systems engineer',
    principles_used: ['Scarcity'],
    explanation:
      'Names the unglamorous truth of the role instead of dressing it up, which is what makes it land with the small number of people who actually want it.',
    hashtags: ['hiring', 'remotework'],
    tone_used: 'conversational',
    is_favorited: false,
    collection_id: null,
    daysAgo: 31,
  },
  {
    id: 'post-dev-9',
    platform: 'instagram',
    content:
      'Three teams.\nFour rebuilds of the same button.\n\nThat was the day we started Northwind.',
    original_text: 'the founding story, as short as it goes',
    principles_used: ['Curiosity Gap'],
    explanation:
      'The whole story in eleven words. The specificity of the numbers is what makes it feel true rather than like a slogan.',
    hashtags: ['startup', 'designsystems'],
    tone_used: 'bold',
    is_favorited: false,
    collection_id: null,
    daysAgo: 38,
  },
];

/** Build the seeded posts, generating their images against the live DOM. */
export function buildDevPosts(): SavedPost[] {
  return POST_SEEDS.map((s) => {
    const image = s.image ? brandImage(s.image[0], s.image[1]) : null;
    return {
      id: s.id,
      platform: s.platform,
      content: s.content,
      original_text: s.original_text,
      principles_used: s.principles_used,
      explanation: s.explanation,
      hashtags: s.hashtags,
      posting_tips: null,
      tone_used: s.tone_used,
      is_favorited: s.is_favorited,
      collection_id: s.collection_id,
      image_base64: image,
      image_mime_type: image ? 'image/png' : null,
      text_overlay_data: null,
      created_at: daysAgo(s.daysAgo),
    };
  });
}

export function buildDevCollections(): Collection[] {
  return [
    {
      id: COLLECTION_LAUNCH,
      name: 'Launch week',
      description: 'The tokens-as-code announcement run.',
      post_count: 0, // recomputed from posts on read — never stored stale
      created_at: daysAgo(5),
      updated_at: daysAgo(2),
    },
    {
      id: COLLECTION_EVERGREEN,
      name: 'Evergreen',
      description: 'Posts worth re-running every quarter.',
      post_count: 0,
      created_at: daysAgo(20),
      updated_at: daysAgo(6),
    },
  ];
}

/**
 * Collections with `post_count` computed from the live posts, so the count on a
 * collection card can never disagree with the number of posts you get when you
 * open it — the exact class of contradiction the dashboard used to ship.
 */
export function collectionsWithCounts(): Collection[] {
  const posts = devRead<SavedPost[]>(DEV_KEYS.posts, []);
  return devRead<Collection[]>(DEV_KEYS.collections, []).map((c) => ({
    ...c,
    post_count: posts.filter((p) => p.collection_id === c.id).length,
  }));
}

// ============================================================================
// DASHBOARD — derived, never fabricated
// ============================================================================

/**
 * The whole point of this function: `/dashboard` used to hardcode
 * `total_posts: 42` while `/posts` returned an empty list, so the same app in
 * the same session contradicted itself. Every number below is now computed from
 * the seeded posts. If you delete a post, the dashboard drops by one.
 *
 * The time/money coefficients mirror the backend's dashboard route (15 min and
 * ₹500 of agency time per post) so the dev numbers are the same *kind* of
 * number the real ones are.
 */
export function deriveDashboardStats(posts: SavedPost[]): DashboardStats {
  const MINUTES_PER_POST = 15;
  const INR_PER_POST = 500;

  const monthStart = new Date();
  monthStart.setDate(1);
  monthStart.setHours(0, 0, 0, 0);
  const isThisMonth = (p: SavedPost) => new Date(p.created_at) >= monthStart;

  const withImage = posts.filter((p) => !!p.image_base64);

  const counts = new Map<string, number>();
  for (const p of posts) counts.set(p.platform, (counts.get(p.platform) ?? 0) + 1);
  const platform_breakdown = [...counts.entries()]
    .map(([platform, count]) => ({ platform, count }))
    .sort((a, b) => b.count - a.count);

  // Consecutive days, counting back from the most recent post's day.
  const days = [...new Set(posts.map((p) => dateOnly(p.created_at)))].sort().reverse();
  let streak_count = 0;
  if (days.length) {
    streak_count = 1;
    for (let i = 1; i < days.length; i++) {
      const prev = new Date(days[i - 1]).getTime();
      const cur = new Date(days[i]).getTime();
      if (prev - cur !== DAY) break;
      streak_count++;
    }
  }

  const oldest = posts.reduce<number>(
    (acc, p) => Math.min(acc, new Date(p.created_at).getTime()),
    Date.now(),
  );

  return {
    total_posts: posts.length,
    total_images: withImage.length,
    time_saved_minutes: posts.length * MINUTES_PER_POST,
    money_saved_inr: posts.length * INR_PER_POST,
    posts_this_month: posts.filter(isThisMonth).length,
    images_this_month: withImage.filter(isThisMonth).length,
    streak_count,
    platform_breakdown,
    milestone: posts.length ? `Shipped ${posts.length} posts` : null,
    joined_days_ago: Math.max(1, Math.round((Date.now() - oldest) / DAY)),
  };
}

// ============================================================================
// GENERATION OUTPUT — what a dev "Generate posts" actually returns
// ============================================================================
// The old stream mock returned MOCK_GENERATED_POSTS — Acme Corp boilerplate,
// from a different company than the seeded workspace, with "(variation 2)"
// literally appended to make up the numbers. Generating in dev therefore told
// you nothing about whether the composer renders real output. These variants
// are Northwind's, in Northwind's voice, so the tool's output agrees with the
// workspace the rest of the app is showing.
//
// `__zupaTourDev.simulateGeneration()` and the real dev generate path both read
// from here, so the harness simulates exactly what the mock produces.

export const DEV_VARIANTS: Record<string, string[]> = {
  linkedin: [
    "Most design systems die in a Figma file nobody opens.\n\nWe moved ours to tokens-as-code — defined once, consumed everywhere. Designers and engineers finally read from the same source.\n\nWeek one: 14 components migrated, 3 things broke, all 3 fixed the same day. That last part is the point.",
    'A question for design leads:\n\nhow many times has your team rebuilt the same button this quarter?\n\nWe got tired of counting, so we built a single source of truth. The whole pitch is: stop hand-syncing.',
    "We shipped tokens-as-code this week.\n\nSome things broke. We fixed them in a day, because the definitions live in one place now instead of scattered across three tools.\n\nThat is the difference a source of truth makes.",
  ],
  instagram: [
    'Stop hand-syncing your colours.\n\nDefine them once. Consume them everywhere.\n\nThat is the whole pitch.',
    'Three teams. Four rebuilds of the same button.\n\nThat was the day we started Northwind.',
    'Your design system should live where engineers already work — not in a doc nobody opens.',
  ],
  twitter: [
    'design system maturity, honestly:\n\n1. a figma file\n2. a figma file + a readme\n3. a package nobody upgrades\n4. tokens in code, one source of truth\n\nmost teams think they are at 4. most are at 2.',
    'stop hand-syncing your colours.\n\ndefine them once. consume them everywhere. that is the whole pitch.',
    'we shipped tokens-as-code. three things broke. all three were fixed the same day.\n\nthat is the actual pitch — not "nothing breaks", but "a break costs you an hour instead of a week".',
  ],
  facebook: [
    'Most design systems die in a file nobody opens.\n\nWe moved ours into the code, where the engineers already are. Here is what changed in the first week — including the parts that broke.',
    'Three teams rebuilding the same button was the day we started Northwind.\n\nIf that sounds familiar, the problem is not your designers.',
    'One definition of your brand, consumed everywhere. That is the entire idea.',
  ],
};

const DEV_HASHTAGS = [
  ['#designsystems', '#devx'],
  ['#designops', '#frontend'],
  ['#productdesign', '#shipit'],
];

// Typed, NOT cast. The first version of this was handed to the composer as
// `{principles, explanation} as any`, which typechecked and then crashed
// PsychologyReasoningCard at runtime on `reasoning[pid]` — an `as any` on a mock
// is the same lie as an empty one, just deferred to render time.
export const DEV_PSYCH_SELECTION: PrincipleSelectionEvent = {
  principles: ['Social Proof', 'Authority'],
  explanation:
    'Design-system leads trust peers who have shipped. Social proof (real first-week results) plus authority (we built this, here is what broke) lands harder than feature claims.',
  reasoning: {
    'Social Proof': {
      why_selected:
        'The audience evaluates tools by what other teams already trust — concrete results from a real rollout do the persuading.',
      structural_position: null,
      synergy: 'Pairs with Authority — proof makes the authority claim credible.',
    },
    Authority: {
      why_selected:
        'A builder sharing what broke and how they fixed it reads as credible, not salesy.',
      structural_position: null,
      synergy: null,
    },
  },
  avoided: {
    Scarcity: 'No genuine time or quantity limit — it would read as manufactured urgency.',
  },
  confidence: 0.88,
};

/**
 * Precheck warnings, seeded so the chips — and the remedies attached to them — are reachable
 * in dev at all. The engine flags its own output ("names a product this company doesn't have",
 * "none of these options use one of your real facts") and the app has to offer a way out. With
 * no warnings in the mock, that entire path was invisible locally, which is exactly how the
 * inert-chip bug survived.
 *
 * `no_fact` is a SET-level chip (the whole batch used none of the real facts) and gets the
 * "Add facts" CTA. Everything else is per-option and gets "Edit this option".
 *
 * Sprint 28: the third option seeds a `structure` chip (a per-platform format-structure flag)
 * so the format-structure rendering path is reachable in dev — a violating draft shows the
 * chip, a clean option shows nothing new.
 */
const DEV_WARNINGS: Array<Array<{ type: string; chip_text: string }>> = [
  [{ type: 'no_fact', chip_text: 'none of these options use one of your real facts' }],
  [{ type: 'invented_product', chip_text: "names a product this company doesn't have" }],
  [{ type: 'structure', chip_text: 'external link in the body — LinkedIn cuts reach on outbound links; move it to the first comment' }],
];

/** The per-platform payload the composer receives (both stream and non-stream). */
export function buildDevGeneratedPlatform(platform: string) {
  const variants = DEV_VARIANTS[platform] ?? DEV_VARIANTS.linkedin;
  return {
    platform,
    variants,
    variations: variants.map((content, i) => ({
      content,
      hook_type: ['bold-statement', 'question', 'story'][i] ?? 'story',
      hashtags: DEV_HASHTAGS[i] ?? DEV_HASHTAGS[0],
      warnings: DEV_WARNINGS[i] ?? [],
    })),
    hashtags: DEV_HASHTAGS,
    hookTypes: ['bold-statement', 'question', 'story'],
    principles_used: DEV_PSYCH_SELECTION.principles,
    principlesUsed: DEV_PSYCH_SELECTION.principles,
    explanation: DEV_PSYCH_SELECTION.explanation,
    tone_used: 'professional',
    recommended_image_index: 0,
    recommendedImageIndex: 0,
  };
}

/**
 * How long a real generation takes, per platform. The old mock used 800ms; the
 * real single-pass Gemini call is 10-18s per platform, which is why the
 * "still working" copy and the disabled states were never exercised in dev.
 * Deliberately honest — `__zupaDev.speed('fast')` collapses it when you are
 * iterating and don't need the slow path.
 */
export const DEV_GEN_MS_PER_PLATFORM = 11_000;

// ============================================================================
// THE FEED — the logged-in front door
// ============================================================================
// Mixed on purpose: autopilot drafts and a disruption alert, one already
// carrying credibility issues, so FeedCard's warning path is reachable in dev.
//
// credibility_score is an INT 0..100, and the auto-approve bar is 70
// (backend/app/services/prechecks.py: `"score": int 0..100`, CREDIBILITY_BAR =
// 70). The first version of this seed used a 0-1 float, which rendered as
// "0.86/100" and put every draft below the bar — so the clean drafts were all
// flagged "needs review" and the flag meant nothing. Keep these as integers.

export function buildDevFeed(): AutopilotEntry[] {
  return [
    {
      id: 'feed-dev-1',
      case_study_id: null,
      date: inDays(1),
      platform: 'linkedin',
      content:
        'The design-system tax nobody puts on the roadmap:\n\nevery week your tokens live in two places, someone somewhere is shipping a colour that is almost right.\n\nAlmost right is the expensive kind of wrong. It does not break the build — it just quietly erodes the brand until a customer notices before you do.',
      kind: 'autopilot_post',
      status: 'pending_approval',
      credibility_score: 91,
      issues: null,
    },
    {
      id: 'feed-dev-2',
      case_study_id: null,
      date: inDays(1),
      platform: 'instagram',
      content:
        'Almost right is the expensive kind of wrong.\n\nA colour that is one hex off does not break the build. It just quietly erodes the brand.',
      kind: 'autopilot_post',
      status: 'pending_approval',
      credibility_score: 88,
      issues: null,
    },
    {
      id: 'feed-dev-3',
      case_study_id: 'cs-dev-1',
      date: inDays(2),
      platform: 'linkedin',
      content:
        'We cut Meridian Health\'s design-to-ship time by 62% and saved them roughly $400k a year — the best result anyone in this industry has ever posted.\n\nEvery team should be doing this by now.',
      kind: 'autopilot_post',
      status: 'pending_approval',
      // Deliberately flawed: this is the draft that SHOULD be caught. The
      // superlative and the round dollar figure are exactly what the
      // credibility precheck exists to flag, and without a draft like this in
      // the seed the warning UI is unreachable in dev.
      credibility_score: 42,
      issues: [
        {
          type: 'fact',
          quote: 'saved them roughly $400k a year',
          why: 'No fact in your Facts & Achievements supports a dollar figure for Meridian Health. The case study records a time saving, not a cost saving.',
          suggestion: 'Cite the 62% design-to-ship reduction, which is in the case study, and drop the dollar claim.',
        },
        {
          type: 'term',
          quote: 'the best result anyone in this industry has ever posted',
          why: 'Unverifiable superlative. Nothing on record supports a category-wide claim.',
          suggestion: 'Replace with the specific, defensible number.',
        },
        {
          type: 'tone',
          quote: 'Every team should be doing this by now.',
          why: 'Scolding. Your voice profile explicitly avoids lecturing the reader.',
          suggestion: 'Make it an invitation rather than an instruction.',
        },
      ],
    },
    {
      id: 'feed-dev-4',
      case_study_id: null,
      date: inDays(0),
      platform: 'linkedin',
      content:
        'Figma just shipped variables that resolve across modes.\n\nIf you have been waiting to move your tokens out of a spreadsheet, the last excuse died this morning.\n\nHere is what it changes for teams already running tokens-as-code — and the one thing it still does not solve.',
      kind: 'disruption_post',
      status: 'pending_approval',
      credibility_score: 86,
      issues: null,
    },
  ];
}

/** Autopilot's own pending list — the calendar-side view of the same drafts. */
export function buildDevPendingAutopilot(): AutopilotEntry[] {
  return buildDevFeed().filter((e) => e.kind === 'autopilot_post');
}

export function buildDevDisruptions(): AutopilotEntry[] {
  return buildDevFeed().filter((e) => e.kind === 'disruption_post');
}

/** A freshly-detected disruption — what `checkDisruptionsNow` returns. */
export function buildFreshDisruption(): AutopilotEntry[] {
  return [
    {
      id: `disruption-dev-${Date.now()}`,
      case_study_id: null,
      date: inDays(0),
      platform: 'linkedin',
      content:
        'A competitor just open-sourced their token pipeline.\n\nThe interesting part is not the code. It is that they have now made the thing you charge for into table stakes — and the teams evaluating you this quarter will notice.\n\nHere is how we would respond if we were you.',
      kind: 'disruption_post',
      status: 'pending_approval',
      credibility_score: 79,
      issues: null,
    },
  ];
}

// ============================================================================
// CONNECTED ACCOUNTS
// ============================================================================
// One healthy, one erroring. The error row exists so the "reconnect" path is
// reachable in dev — with an all-green list it never renders.

export function buildDevSocialAccounts(): ConnectedSocialAccount[] {
  return [
    {
      id: 'social-dev-1',
      platform: 'linkedin',
      account_name: 'Northwind Studio',
      account_username: 'northwind-studio',
      account_avatar_url: null,
      account_type: 'organization',
      is_active: true,
      last_used_at: daysAgo(2),
      last_error: null,
    },
    {
      id: 'social-dev-2',
      platform: 'instagram',
      account_name: 'Northwind Studio',
      account_username: 'northwind.studio',
      account_avatar_url: null,
      account_type: 'business',
      is_active: true,
      last_used_at: daysAgo(3),
      last_error: null,
    },
    {
      // is_active stays TRUE with a last_error set. Connections.tsx filters the
      // list on `is_active` (line ~346) but renders `last_error` on the accounts
      // it does show — so an INACTIVE account is simply hidden, and seeding the
      // error that way would have made the failure banner unreachable. An active
      // account whose last post failed is also the truer state: the connection
      // still exists, the last attempt did not.
      id: 'social-dev-3',
      platform: 'facebook',
      account_name: 'Northwind Studio',
      account_username: 'northwindstudio',
      account_avatar_url: null,
      account_type: 'page',
      is_active: true,
      last_used_at: daysAgo(31),
      last_error: 'Token expired — reconnect to keep posting to this Page.',
    },
  ];
}

// ============================================================================
// CASE STUDIES
// ============================================================================

export function buildDevCaseStudies(): CaseStudyListItem[] {
  return [
    {
      id: 'cs-dev-1',
      status: 'generated',
      title: 'How Meridian Health cut design-to-ship time by 62%',
      client_name: 'Meridian Health',
      campaign_count: 4,
      created_at: daysAgo(11),
    },
    {
      id: 'cs-dev-2',
      status: 'generated',
      title: 'Vantage Freight: one design system, nine product teams',
      client_name: 'Vantage Freight',
      campaign_count: 3,
      created_at: daysAgo(27),
    },
    {
      id: 'cs-dev-3',
      status: 'draft',
      title: 'Untitled case study',
      client_name: 'Kestrel Analytics',
      campaign_count: 0,
      created_at: daysAgo(1),
    },
  ];
}

/**
 * Detail for a seeded case study. Without this, `listCaseStudies` returning
 * rows would just move the dead end one click deeper — the list would populate
 * and every row would 404. A seeded list whose rows do not open is a new lie,
 * not a fix.
 */
export function buildDevCaseStudyDetail(id: string): CaseStudyDetailResponse {
  const row = buildDevCaseStudies().find((c) => c.id === id) ?? buildDevCaseStudies()[0];
  const isDraft = row.status === 'draft';

  return {
    id: row.id,
    workspace_id: DEV_WORKSPACE_ID,
    status: row.status,
    created_at: row.created_at,
    target_keywords: ['design system', 'tokens-as-code', 'design ops'],
    keyword_warnings: [],
    input: {
      clientName: row.client_name,
      clientIndustry: row.client_name === 'Meridian Health' ? 'Healthcare' : 'Logistics',
      clientGeography: 'North America',
      category: 'Design systems',
      engagementYear: String(new Date().getFullYear() - 1),
      engagementDuration: '6 months',
      clientBackground:
        `${row.client_name} ran nine product teams off three separate component libraries, each with its own definition of the same brand.`,
      clientAmbition: 'One coherent product surface, without freezing delivery for a quarter.',
      challenges:
        'Three component libraries, no shared tokens, a brand refresh already committed to the board.',
      solution:
        'Migrated every team onto a single tokens-as-code source of truth, one team at a time, with the old libraries kept alive behind an alias layer until the last consumer moved.',
      productsUsed: 'Design System Platform, Migration Service',
      businessImpact:
        'Design-to-ship time fell 62%. The brand refresh shipped on the original date.',
      metric1Value: '62%',
      metric1Label: 'faster design-to-ship',
      metric2Value: '9',
      metric2Label: 'teams migrated',
      metric3Value: '0',
      metric3Label: 'delivery freezes',
      metric4Value: '6mo',
      metric4Label: 'end to end',
      quote:
        'We expected a year of disruption. We got a brand refresh that shipped on time and engineers who stopped arguing about hex codes.',
      quoteAuthor: 'Dana Whitfield',
      quoteRole: 'VP Product Design',
      capabilities: 'Design systems, tokens-as-code, incremental migration',
      keywords: ['design system', 'tokens-as-code'],
    },
    case_study: isDraft
      ? null
      : {
          title: row.title,
          intro:
            `${row.client_name} was running nine product teams off three component libraries. Every team believed it was building the same brand. None of them were.`,
          subIntro: 'A migration that never froze delivery.',
          situationBefore:
            'Three libraries, three definitions of the same colour, and a brand refresh already promised to the board.',
          situationAfter:
            'One source of truth in code. The refresh shipped on its original date.',
          capabilitiesHeading: 'What we brought',
          capabilitiesSub: 'The platform, and the hands to move everyone onto it.',
          capabilities: ['Tokens-as-code platform', 'Incremental migration', 'Alias layer for legacy consumers'],
          challenges: [
            'Nine teams, three component libraries, zero shared tokens.',
            'A brand refresh already committed to a board date.',
            'No team could afford to stop shipping for the migration.',
          ],
          impact: [
            'Design-to-ship time down 62%.',
            'Brand refresh delivered on the original date.',
            'Onboarding for new frontend engineers cut from two weeks to three days.',
          ],
          solution: [
            'Defined every token once, in code, as the single source of truth.',
            'Kept the legacy libraries alive behind an alias layer so no team was ever blocked.',
            'Migrated one team at a time, retiring each old library only when its last consumer moved.',
          ],
          benefits: [
            'One definition of the brand, consumed everywhere.',
            'A break is now a one-line fix, not an archaeology dig.',
            'The design system stopped being a negotiation.',
          ],
          metrics: [
            { value: '62%', label: 'faster design-to-ship' },
            { value: '9', label: 'teams migrated' },
            { value: '0', label: 'delivery freezes' },
            { value: '6mo', label: 'end to end' },
          ],
          quote:
            'We expected a year of disruption. We got a brand refresh that shipped on time and engineers who stopped arguing about hex codes.',
          quoteAuthor: 'Dana Whitfield',
          quoteRole: 'VP Product Design',
        },
    blog: isDraft
      ? null
      : {
          title: 'What nine teams and three component libraries taught us about migrations',
          subtitle: 'The alias layer was the whole trick.',
          readTime: '6 min read',
          body:
            '## The problem was never the code\n\nWhen we met the team at ' +
            row.client_name +
            ', they did not have a design problem. They had a *definition* problem: three libraries, each convinced it held the real brand.\n\n## Why big-bang migrations fail\n\nThe usual advice is to freeze delivery and cut over. No team with a board date can do that, and the ones that try tend to discover their edge cases in production.\n\n## The alias layer\n\nWe kept every legacy import working. Old names resolved to new tokens, so a team that had not migrated yet simply kept shipping — and every build they shipped was quietly already using the new source of truth.\n\n## What we would do again\n\nMigrate one team at a time. Retire a library only when its last consumer is gone. Let the alias layer absorb the mess.',
        },
    campaign_entries: isDraft ? [] : buildCampaignEntries(row.id),
  };
}

function buildCampaignEntries(caseStudyId: string): CampaignEntryOut[] {
  return [
    {
      id: `camp-${caseStudyId}-1`,
      case_study_id: caseStudyId,
      date: inDays(2),
      platform: 'linkedin',
      content:
        'Nine product teams. Three component libraries. One brand refresh already promised to the board.\n\nHere is how Meridian Health shipped it on the original date — without freezing delivery for a single sprint.',
      kind: 'campaign_post',
      angle: 'The setup',
      score: 0.89,
    },
    {
      id: `camp-${caseStudyId}-2`,
      case_study_id: caseStudyId,
      date: inDays(5),
      platform: 'linkedin',
      content:
        'The trick that made a nine-team migration survivable was not the tokens. It was the alias layer.\n\nEvery legacy import kept working. Teams that had not migrated yet kept shipping — and every build they shipped was already using the new source of truth without them knowing.',
      kind: 'campaign_post',
      angle: 'The mechanism',
      score: 0.93,
    },
    {
      id: `camp-${caseStudyId}-3`,
      case_study_id: caseStudyId,
      date: inDays(9),
      platform: 'instagram',
      content: '62% faster design-to-ship.\n\nNine teams. Zero delivery freezes.',
      kind: 'campaign_post',
      angle: 'The number',
      score: 0.84,
    },
    {
      id: `camp-${caseStudyId}-4`,
      case_study_id: caseStudyId,
      date: inDays(14),
      platform: 'linkedin',
      content:
        '"We expected a year of disruption. We got a brand refresh that shipped on time and engineers who stopped arguing about hex codes."\n\n— Dana Whitfield, VP Product Design',
      kind: 'campaign_post',
      angle: 'The proof',
      score: 0.91,
    },
  ];
}

/**
 * Calendar entries for a given month. Only returns the ones that actually fall
 * in the requested month — a calendar mock that returns every entry regardless
 * of the month would make month navigation look like it works when it does not.
 */
export function buildDevCampaignCalendar(year: number, month: number): CampaignEntryOut[] {
  return buildCampaignEntries('cs-dev-1').filter((e) => {
    const d = new Date(e.date);
    return d.getFullYear() === year && d.getMonth() + 1 === month;
  });
}

// ============================================================================
// FACT BANK
// ============================================================================

export function buildDevFacts(): Fact[] {
  return [
    {
      id: 'fact-dev-1',
      kind: 'fact',
      text: 'Onboarding time for a new frontend engineer dropped from two weeks to three days after the tokens-as-code migration.',
      category: 'proof',
      source: 'user',
      created_at: daysAgo(20),
      updated_at: daysAgo(20),
    },
    {
      id: 'fact-dev-2',
      kind: 'fact',
      text: 'Meridian Health cut design-to-ship time by 62% across nine product teams.',
      category: 'proof',
      source: 'research_confirmed',
      created_at: daysAgo(11),
      updated_at: daysAgo(11),
    },
    {
      id: 'fact-dev-3',
      kind: 'stance',
      text: 'A design system that lives in a Figma file is documentation, not a system. The system is whatever the code actually reads.',
      category: 'opinion',
      source: 'user',
      created_at: daysAgo(18),
      updated_at: daysAgo(18),
    },
    {
      id: 'fact-dev-4',
      kind: 'stance',
      text: 'Big-bang migrations are a choice, not a necessity. Incremental is almost always available if you are willing to build the alias layer.',
      category: 'opinion',
      source: 'user',
      created_at: daysAgo(15),
      updated_at: daysAgo(15),
    },
    {
      id: 'fact-dev-5',
      kind: 'boundary',
      text: 'Never claim a specific dollar figure for a client outcome unless the client has published it themselves.',
      category: 'legal',
      source: 'user',
      created_at: daysAgo(25),
      updated_at: daysAgo(25),
    },
    {
      id: 'fact-dev-6',
      kind: 'boundary',
      text: 'Do not name a client by name in a post unless the case study for them is already public.',
      category: 'legal',
      source: 'user',
      created_at: daysAgo(25),
      updated_at: daysAgo(25),
    },
  ];
}

// ============================================================================
// WORKSPACE EXAMPLES
// ============================================================================

export function buildDevExamples(): WorkspaceExample[] {
  return [
    {
      id: 'example-dev-1',
      platform: 'linkedin',
      content:
        'We shipped tokens-as-code this week. Here is what broke and how we fixed it.',
      created_at: daysAgo(30),
    },
    {
      id: 'example-dev-2',
      platform: 'linkedin',
      content:
        'Most design systems die in a Figma file nobody opens. Ours lives where engineers already are.',
      created_at: daysAgo(30),
    },
    {
      id: 'example-dev-3',
      platform: 'instagram',
      content:
        'Stop hand-syncing colors. Define them once, consume them everywhere. That is the whole pitch.',
      created_at: daysAgo(30),
    },
  ];
}

// ============================================================================
// GENERATION HISTORY
// ============================================================================

export function buildDevSessions(): GenerationSessionSummary[] {
  return [
    {
      id: 'sess-dev-1',
      title: 'Tokens-as-code launch post',
      prompt_text: 'post about why we moved to tokens-as-code and what happened in week one',
      platforms: ['linkedin', 'instagram'],
      turn_count: 2,
      created_at: daysAgo(2),
      updated_at: daysAgo(2),
    },
    {
      id: 'sess-dev-2',
      title: 'The founding story',
      prompt_text: 'post about the founding story — three teams rebuilding the same button',
      platforms: ['linkedin'],
      turn_count: 1,
      created_at: daysAgo(6),
      updated_at: daysAgo(6),
    },
    {
      id: 'sess-dev-3',
      title: 'What broke when we shipped',
      prompt_text: 'retro on what broke when we shipped tokens-as-code',
      platforms: ['linkedin', 'twitter'],
      turn_count: 3,
      created_at: daysAgo(9),
      updated_at: daysAgo(8),
    },
    {
      id: 'sess-dev-4',
      title: 'Onboarding time post',
      prompt_text: 'post about how onboarding time dropped after the migration',
      platforms: ['linkedin'],
      turn_count: 1,
      created_at: daysAgo(24),
      updated_at: daysAgo(24),
    },
  ];
}

/**
 * Detail for a history row — the generated content it produced, replayed into
 * the composer when you click it. Same reasoning as the case-study detail: a
 * seeded list whose rows cannot be opened is not a fix.
 */
export function buildDevSessionDetail(sessionId: string): GenerationSessionDetail {
  const s = buildDevSessions().find((x) => x.id === sessionId) ?? buildDevSessions()[0];
  const post = POST_SEEDS.find((p) => p.original_text === s.prompt_text);

  const variantsFor = (platform: string): string[] => {
    const base = post?.content ?? `A generated ${platform} post about ${s.prompt_text}.`;
    return [
      base,
      `${base.split('\n\n')[0]}\n\nWe wrote up the whole thing — the short version is that a single source of truth changes what a mistake costs you.`,
      `${base.split('\n\n')[0]}\n\nThe long version is in the comments. The short version: stop hand-syncing.`,
    ];
  };

  const generated_data: Record<string, unknown> = {};
  for (const platform of s.platforms) {
    generated_data[platform] = {
      variants: variantsFor(platform),
      hashtags: [
        ['#designsystems', '#devx'],
        ['#designops', '#frontend'],
        ['#productdesign', '#shipit'],
      ],
      hookTypes: ['bold-statement', 'question', 'story'],
      principlesUsed: post?.principles_used ?? ['Social Proof'],
      explanation: post?.explanation ?? 'Anchored on the principles your audience responds to.',
      recommendedImageIndex: 0,
    };
  }

  return {
    id: s.id,
    workspace_id: DEV_WORKSPACE_ID,
    user_id: 'dev-user-123',
    title: s.title,
    prompt_text: s.prompt_text,
    platforms: s.platforms,
    platform_tones: null,
    service_ids: null,
    persona_ids: null,
    include_images: false,
    created_at: s.created_at,
    updated_at: s.updated_at,
    turns: [
      {
        id: `${s.id}-turn-1`,
        session_id: s.id,
        turn_number: 1,
        prompt_text: s.prompt_text,
        force_regenerate: false,
        generated_data,
        shared_principles: post?.principles_used ?? ['Social Proof'],
        shared_explanation: post?.explanation ?? null,
        unified_images: null,
        ai_generated_images: null,
        ai_carousel_data: null,
        generation_timing: null,
        created_at: s.created_at,
      },
    ],
  };
}
